import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { Fragment, useEffect } from 'react';
import Location from './Location';
import Dates from './Dates';
import Passengers from './Passengers';
import { FormType, newBound, TripTypes } from '../../shared/models/form';
import { useTranslation } from 'react-i18next';
import PromoCode from './PromoCode';
import AddTripButton from './AddTripButton';
import RemoveTripButton from './RemoveTripButton';

export const SearchFields = () => {
  const { t } = useTranslation();
  const { control, getValues, setValue } = useFormContext<FormType>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'bounds',
    rules: { maxLength: 4 },
  });

  const tripType = useWatch({ control, name: 'tripType' });
  const isTripTypeRoundTrip = tripType === TripTypes.roundTrip;
  const isTripTypeMultiCity = tripType === TripTypes.multiCity;

  useEffect(() => {
    if (!isTripTypeRoundTrip) {
      setValue('bounds.0.return', '');
    }
  }, [isTripTypeRoundTrip, setValue]);

  useEffect(() => {
    if (isTripTypeMultiCity) {
      append(newBound, { shouldFocus: false });
    }
  }, [isTripTypeMultiCity, append]);

  useEffect(() => {
    if (!isTripTypeMultiCity) {
      for (let i = 1; i < fields.length; i++) {
        remove(i);
      }
    }
  }, [isTripTypeMultiCity, fields.length, remove]);

  const dateSelectHandler = (index: number) => {
    if (!isTripTypeMultiCity) {
      return;
    }

    const currentValue = getValues(`bounds.${index}.departure`);
    for (let i = index + 1; i < fields.length; i++) {
      const nextValue = getValues(`bounds.${i}.departure`);
      if (nextValue && currentValue > nextValue) {
        setValue(`bounds.${i}.departure`, currentValue);
      }
    }
  };

  return (
    <div
      className={`grid ${
        isTripTypeMultiCity
          ? 'md:grid-cols-form-multi-city gap-4'
          : 'md:grid-cols-form gap-y-4 gap-x-14'
      } mt-4`}
    >
      {fields.map((field, index) => (
        <Fragment key={field.id}>
          {isTripTypeMultiCity && (
            <div className="md:col-span-4 pl-2 font-semibold">
              {t('Trip', { number: index + 1 })}
            </div>
          )}
          <Location
            label={t('From')}
            placeholder={t('Select Origin')}
            type="origin"
            fieldIndex={index}
          />
          <Location
            label={t('To')}
            placeholder={t('Select Destination')}
            type="destination"
            fieldIndex={index}
          />
          <div
            className={`${
              isTripTypeMultiCity
                ? 'md:grid-cols-1'
                : `grid ${isTripTypeRoundTrip && 'md:grid-cols-form gap-y-4'}`
            }`}
          >
            <Dates
              isTripTypeRoundTrip={isTripTypeRoundTrip}
              label={t('Depart')}
              type="departure"
              fieldIndex={index}
              onDateSelect={dateSelectHandler}
            />
            {isTripTypeRoundTrip && (
              <Dates
                isTripTypeRoundTrip={isTripTypeRoundTrip}
                label={t('Return')}
                type="return"
                fieldIndex={index}
              />
            )}
          </div>
          {isTripTypeMultiCity && index > 1 && <RemoveTripButton onClick={() => remove(index)} />}
        </Fragment>
      ))}
      {isTripTypeMultiCity && <AddTripButton onClick={() => append(newBound)} />}
      <div
        className={`grid ${
          isTripTypeMultiCity ? 'md:col-span-4 md:grid-cols-form-multi-city' : 'md:grid-cols-form'
        } gap-4`}
      >
        <Passengers />
        <PromoCode />
      </div>
    </div>
  );
};
